import {
  ContentHeader,
  FilterDefs,
  GridSortConfig,
  GridTableLayout,
  HeaderAction,
  multiFilter,
  RowStyles,
  toggleFilter,
  useGridTableLayoutState,
  useRightPane,
} from "@homebound/beam";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { baseDownloadUrl } from "src/context";
import {
  Maybe,
  ProductOfferingOrder,
  ProductOfferingsFilter,
  ProductOfferingVersionMode,
  ReadyPlanStatus,
} from "src/generated/graphqlTypes";
import {
  defaultGlobalProductOfferingsFilter,
  defaultGlobalProductOfferingsOrder,
  defaultGlobalProductOfferingsPage,
} from "src/routes/libraries/product-offerings/GlobalProductOfferings";
import { ProductOfferingOverviewPanel } from "src/routes/libraries/product-offerings/overview/ProductOfferingOverviewPanel";
import {
  ProductOfferingsOverviewPageQuery,
  useProductOfferingsOverview_SaveProductOfferingMutation,
  useProductOfferingsOverviewPageQuery,
} from "src/routes/libraries/product-offerings/overview/ProductOfferingsOverviewTab.generated";
import {
  createProductOfferingsOverviewColumns,
  createProductOfferingsOverviewRows,
  ProductOfferingsOverviewTableRow,
  productOfferingStatusTag,
} from "src/routes/libraries/product-offerings/overview/ProductOfferingsOverviewTable";
import { addEntityParam, createProductOfferingConfigsUrl, createProductOfferingUrl } from "src/RouteUrls";
import { parseOrder, toOrder } from "src/utils/ordering";
import { openInSelf } from "src/utils/window";

type ProductOfferingsOverviewTabProps = { projectId: string };

export function ProductOfferingsOverviewTab(props: ProductOfferingsOverviewTabProps) {
  const { projectId } = props;
  const navigate = useNavigate();
  // TODO: These are stubbed filters. The actual filters will be added in: https://linear.app/homebound-team/issue/PLAN-2008/febe-implement-overview-table-filters
  // It might require a BE change to support the filters, tbd, so I'm splitting this into a separate ticket
  const filterDefs: FilterDefs<ProductOfferingsOverviewFilter> = useMemo(
    () => ({
      status: multiFilter({
        label: "POF Status",
        options: [ReadyPlanStatus.Active, ReadyPlanStatus.Draft].map((code) => ({
          code,
          name: productOfferingStatusTag[code].text,
        })),
        getOptionValue: (o) => o.code,
        getOptionLabel: (o) => o.name,
      }),
      archived: toggleFilter({ label: "Show Archived" }),
    }),
    [],
  );
  const layoutState = useGridTableLayoutState<ProductOfferingsOverviewFilter>({
    persistedFilter: { filterDefs, storageKey: "project-product-offerings-overview-filter" },
    search: "server",
  });
  const [orderBy, setOrderBy] = useState<ProductOfferingOrder>(defaultGlobalProductOfferingsOrder);
  const [selectedPofId, setSelectedPofId] = useState<string>();
  const { openRightPane, isRightPaneOpen } = useRightPane();
  const query = useProductOfferingsOverviewPageQuery({
    variables: {
      filter: mapToFilter(layoutState.filter, layoutState.searchString, projectId),
      orderBy,
      page: defaultGlobalProductOfferingsPage,
    },
  });
  const [saveProductOffering] = useProductOfferingsOverview_SaveProductOfferingMutation();
  const sorting: GridSortConfig = useMemo(
    () => ({
      on: "server",
      onSort: (key, direction) => setOrderBy(toOrder(key, direction)),
      value: parseOrder(orderBy),
    }),
    [orderBy],
  );
  const columns = useMemo(
    () =>
      createProductOfferingsOverviewColumns({
        projectId,
        onToggle: (id, input) => saveProductOffering({ variables: { input: { id, ...input } } }),
      }),
    [projectId, saveProductOffering],
  );
  const rowStyles: RowStyles<ProductOfferingsOverviewTableRow> = useMemo(
    () => ({
      productOffering: {
        onClick: ({ id }) => {
          setSelectedPofId(id);
          openRightPane({ content: <ProductOfferingOverviewPanel key={id} productOfferingId={id} /> });
        },
      },
    }),
    [openRightPane],
  );
  const createRows = useCallback(
    (data: ProductOfferingsOverviewPageQuery | undefined) =>
      createProductOfferingsOverviewRows(data?.productOfferings.entities ?? []),
    [],
  );
  const maybeFetchNextPage = useCallback(async () => {
    if (query.data?.productOfferings.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: {
          page: { offset: query.data.productOfferings.entities.length, limit: defaultGlobalProductOfferingsPage.limit },
        },
      });
    }
  }, [query]);
  const actions: HeaderAction[] = [
    {
      label: "Create",
      variant: "secondary",
      onClick: () => navigate(createProductOfferingUrl(addEntityParam, undefined, undefined, projectId)),
    },
    // TODO: Bulk publish is a separate ticket: https://linear.app/homebound-team/issue/PLAN-2002/befe-implement-bulk-publish-on-pofs-v2-overview-tab
    { label: "Publish", variant: "primary", onClick: () => {} },
    // Same actions as the v1 project list
    {
      kind: "menu",
      trigger: { icon: "verticalDots", variant: "outline" },
      tooltip: "Actions",
      items: [
        { label: "Compare Configurations", onClick: createProductOfferingConfigsUrl(undefined, projectId) },
        {
          label: "Download Plan Package Diff",
          onClick: () => {
            const params = new URLSearchParams({ type: "planPackageScopeDiff", projectId });
            openInSelf(`${baseDownloadUrl()}/xlsx?${params.toString()}`);
          },
        },
      ],
    },
  ];

  return (
    <>
      <ContentHeader
        title="Overview"
        description="Track the status of every product offering in this community and publish updates."
        actions={actions}
      />
      <GridTableLayout
        layoutState={layoutState}
        emptyFallback="No Product Offerings Found"
        withRightPane={380}
        tableProps={{
          query,
          createRows,
          columns,
          sorting,
          rowStyles,
          as: "virtual",
          infiniteScroll: { onEndReached: maybeFetchNextPage },
          activeRowId: isRightPaneOpen && selectedPofId ? `productOffering_${selectedPofId}` : undefined,
        }}
      />
    </>
  );
}

type ProductOfferingsOverviewFilter = Pick<ProductOfferingsFilter, "status"> & { archived?: Maybe<boolean> };

function mapToFilter(
  filter: ProductOfferingsOverviewFilter,
  searchString: string | undefined,
  projectId: string,
): ProductOfferingsFilter {
  const { status, archived } = filter;
  return {
    ...(status?.nonEmpty ? { effectiveStatus: status } : archived ? {} : defaultGlobalProductOfferingsFilter),
    ...(searchString && { search: searchString }),
    project: [projectId],
    versionMode: ProductOfferingVersionMode.Latest,
  };
}
